import React, { useCallback, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { Button, Checkbox } from 'antd'
import type { CheckboxValueType } from 'antd/lib/checkbox/Group'
import { themeGet } from '@styled-system/theme-get'
import { FilterItemType, FilterMap } from '@/modules/products/viewModels/ProductListViewModel'
import { observer } from 'mobx-react-lite'
import { MinusOutlined, PlusOutlined } from '@ant-design/icons'
import { nanoid } from '@solvook/nanoid'

export const SearchCheckboxWrapper = styled.div<{ showAll?: boolean }>`
	${({ showAll }) =>
		!showAll &&
		css`
			.ant-checkbox-group .ant-checkbox-group-item:nth-child(n + 4) {
				display: none;
			}
		`}

	.ant-checkbox-group {
		display: block;
		overflow: hidden;
		max-height: 230px;
		transition: max-height 0.5s;

		.ant-checkbox-wrapper {
			margin-inline-start: 0;
			margin-top: 12px;

			span {
				font-size: 14px;
				line-height: 1.5;
				color: ${themeGet('text.0', '#1B1B29')};
			}

			@media (max-width: 1200px) {
				display: flex;
				align-items: center;
				justify-content: space-between;
				padding-left: 8px;
				margin-top: 20px;

				span {
					font-size: 18px;

					&:not(.ant-checkbox) {
						order: 1;
						flex: 1;
					}

					&.ant-checkbox {
						order: 2;
					}
				}
			}
			@media (max-width: 768px) {
				span {
					font-size: 16px;
				}
			}

			.ant-checkbox {
				display: inline-block;
				vertical-align: middle;
				width: 16px;
				height: 16px;

				@media (max-width: 1200px) {
					margin-right: 4px;
				}
				@media (max-width: 768px) {
				}

				&.ant-checkbox-checked {
					.ant-checkbox-inner {
						border-color: ${themeGet('primary.0', '#002FA8')};
						background-color: ${themeGet('primary.0', '#002FA8')};
					}

					& + span {
						font-weight: bold;
						color: ${themeGet('primary.0', '#002FA8')};
					}

					@media (max-width: 1200px) {
						&:after {
							opacity: 0;
						}

						.ant-checkbox-inner {
							background-color: transparent;

							&:after {
								width: 9px;
								height: 16px;
								border-color: ${themeGet('primary.1', '#008489')};
							}
						}
					}
					@media (max-width: 768px) {
					}
				}

				input {
					border-radius: 0;
				}

				.ant-checkbox-inner {
					border: 1px solid ${themeGet('border.1', '#E8E8EA')};
					border-radius: 2px;

					@media (max-width: 1200px) {
						border: none;
					}
					@media (max-width: 768px) {
					}
				}
			}
		}
	}

	p {
		font-weight: bold;
	}

	input {
		margin-right: 5px;
	}

	.ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected {
		background-color: ${themeGet('background.0', '#FDFDFF')};
	}
`

export interface CheckboxListType {
	id: number
	title: string
	list: string[]
}

// export interface CheckboxListProps{
//   options: CheckboxListType[]
//
// }

const CheckboxGroup = Checkbox.Group

export interface SearchCheckboxProps {
	filterKey: string
	options: string[] | { label: string; value: string }[]
	selectedItems: FilterMap
	onChange: (filter: string, items: FilterItemType[]) => void
	maxShowItemNum?: number
}

export const SearchCheckbox: React.FC<SearchCheckboxProps> = observer(
	({ filterKey, options, selectedItems, onChange, maxShowItemNum = 3 }) => {
		const [currentOptions, setCurrentOptions] = useState(options.slice(0, maxShowItemNum))
		const [showAllOptions, setShowAllOptions] = useState(false)

		useEffect(() => {
			Object.keys(selectedItems).map((key) => {
				if (key === filterKey) setShowAllOptions(true)
			})
		}, [])

		const toggleShowAllOptions = useCallback(() => {
			setShowAllOptions(!showAllOptions)
		}, [showAllOptions])

		useEffect(() => {
			if (showAllOptions) {
				setCurrentOptions(options)
			}
		}, [options, showAllOptions])

		const handleOnChange = useCallback(
			(list: CheckboxValueType[]) => {
				let items: FilterItemType[]
				if (options[0] && typeof options[0] === 'object') {
					items = list.map((item) => {
						// @ts-ignore
						const option = options.find((option) => option.value === item)

						return {
							id: nanoid(),
							key: filterKey,
							label: option.label,
							value: item as string,
						}
					})
				} else {
					items = list.map((item, idx) => ({
						id: nanoid(),
						value: item.toString(),
						key: filterKey,
					}))
				}

				onChange(filterKey, items)
			},
			[onChange, filterKey],
		)

		return (
			<>
				<SearchCheckboxWrapper
					className={showAllOptions ? 'spread' : ''}
					showAll={showAllOptions}>
					<CheckboxGroup
						options={currentOptions}
						value={selectedItems[filterKey]?.map((item) => item.value)}
						defaultValue={selectedItems[filterKey]?.map((item) => item.value)}
						onChange={handleOnChange}
					/>
				</SearchCheckboxWrapper>

				{options.length > maxShowItemNum && (
					<Button
						type="text"
						icon={showAllOptions ? <MinusOutlined /> : <PlusOutlined />}
						// # TODO 더보기 팝업 작업시 주석으로 교체기
						// onClick={() => {
						// 	onShowModal(tempList, idx)
						// }}
						onClick={toggleShowAllOptions}>
						더보기
					</Button>
				)}
			</>
		)
	},
)
